import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardEvents } from '../contexts/EventContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getStatusLabel, getStatusBadgeClasses } from '../utils/eventStatusMapping';
import * as adminService from '../services/adminService';
import * as eventsService from '../services/eventsService';

const {
  FiUsers,
  FiCalendar,
  FiCheckCircle,
  FiTrendingUp,
  FiEye,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiDollarSign,
  FiUserPlus,
  FiGitBranch,
  FiRefreshCw,
  FiX,
  FiSave,
} = FiIcons;

const ROLE_OPTIONS = ['admin', 'agent', 'partner', 'student'];
const STATUS_OPTIONS = ['active', 'inactive', 'suspended'];

const mapEventRecord = (e) => ({
  id: e.id,
  title: e.title ?? '',
  date: e.date ?? e.start_date ?? '',
  registrations: e.registrations ?? e.registration_count ?? 0,
  price: e.price ?? 0,
  status: e.status ?? 'draft',
  agentId: e.agent_id ?? e.agentId ?? null
});

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { profile, role } = useAuth();
  const { events, leads } = useDashboardEvents();
  const [activeTab, setActiveTab] = useState('overview');
  const [adminStats, setAdminStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [hierarchy, setHierarchy] = useState([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [showAddSubAgent, setShowAddSubAgent] = useState(false);
  const [subAgentParentId, setSubAgentParentId] = useState('');
  const [subAgentChildId, setSubAgentChildId] = useState('');
  const [subAgentRelationship, setSubAgentRelationship] = useState('sub_agent');
  const [savingUser, setSavingUser] = useState(false);
  const [savingHierarchy, setSavingHierarchy] = useState(false);

  const isAdmin = role === 'admin';
  const mappedEvents = (events ?? []).map(mapEventRecord);

  const loadAdminStats = useCallback(async () => {
    if (!isAdmin || !profile?.id) return;
    setStatsLoading(true);
    const { data, error } = await adminService.getAdminStats({ role: 'admin', userId: profile.id });
    setStatsLoading(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load admin stats');
      return;
    }
    setAdminStats(data);
  }, [isAdmin, profile?.id]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin || !profile?.id) return;
    setUsersLoading(true);
    const { data, error } = await adminService.listUsers({ role: 'admin', userId: profile.id });
    setUsersLoading(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load users');
      return;
    }
    setUsers(Array.isArray(data) ? data : []);
  }, [isAdmin, profile?.id]);

  const loadHierarchy = useCallback(async () => {
    if (!isAdmin || !profile?.id) return;
    setHierarchyLoading(true);
    const { data, error } = await adminService.getHierarchyTree({ role: 'admin', userId: profile.id });
    setHierarchyLoading(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load hierarchy');
      return;
    }
    setHierarchy(Array.isArray(data) ? data : []);
  }, [isAdmin, profile?.id]);

  useEffect(() => {
    if (isAdmin && profile?.id) {
      loadAdminStats();
    }
  }, [isAdmin, profile?.id, loadAdminStats]);

  useEffect(() => {
    if (activeTab === 'users' && isAdmin && profile?.id) loadUsers();
  }, [activeTab, isAdmin, profile?.id, loadUsers]);

  useEffect(() => {
    if (activeTab === 'hierarchy' && isAdmin && profile?.id) loadHierarchy();
  }, [activeTab, isAdmin, profile?.id, loadHierarchy]);

  const handleSaveUser = async () => {
    if (!editingUser || !profile?.id) return;
    setSavingUser(true);
    const { data, error } = await adminService.updateUserRoleStatus(
      editingUser.id,
      { role: editRole || undefined, status: editStatus || undefined },
      { role: 'admin', userId: profile.id }
    );
    setSavingUser(false);
    if (error) {
      toast.error(error.message ?? 'Failed to update user');
      return;
    }
    toast.success('User updated');
    setEditingUser(null);
    loadUsers();
  };

  const handleAddSubAgent = async () => {
    if (!subAgentParentId || !subAgentChildId || !profile?.id) {
      toast.error('Parent and child user IDs are required');
      return;
    }
    setSavingHierarchy(true);
    const { data, error } = await adminService.addSubAgent(
      subAgentParentId,
      subAgentChildId,
      subAgentRelationship,
      { role: 'admin', userId: profile.id }
    );
    setSavingHierarchy(false);
    if (error) {
      toast.error(error.message ?? 'Failed to add sub-agent');
      return;
    }
    toast.success('Sub-agent added');
    setShowAddSubAgent(false);
    setSubAgentParentId('');
    setSubAgentChildId('');
    loadHierarchy();
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditRole(user.role ?? '');
    setEditStatus(user.status ?? 'active');
  };

  const stats = isAdmin && adminStats
    ? [
        { title: 'Total Events', value: adminStats.total_events ?? 0, icon: FiCalendar, color: 'bg-blue-500' },
        { title: 'Total Registrations', value: adminStats.total_registrations ?? 0, icon: FiUsers, color: 'bg-emerald-500' },
        { title: 'Total Leads', value: adminStats.total_leads ?? 0, icon: FiCheckCircle, color: 'bg-purple-500' },
        { title: 'Conversion Rate', value: `${adminStats.conversion_rate ?? 0}%`, icon: FiTrendingUp, color: 'bg-amber-500' },
        { title: 'Total Revenue', value: `$${Number(adminStats.total_revenue ?? 0).toLocaleString()}`, icon: FiDollarSign, color: 'bg-teal-500' }
      ]
    : [
        { title: 'Total Events', value: mappedEvents.length, icon: FiCalendar, color: 'bg-blue-500' },
        { title: 'Total Registrations', value: mappedEvents.reduce((s, e) => s + (e.registrations || 0), 0), icon: FiUsers, color: 'bg-emerald-500' },
        { title: '1-1 Completed', value: (leads ?? []).filter(l => l.status === '1_1_completed').length, icon: FiCheckCircle, color: 'bg-purple-500' },
        { title: 'Conversion Rate', value: `${(leads ?? []).length > 0 ? (((leads ?? []).filter(l => l.status === 'converted').length / (leads ?? []).length) * 100).toFixed(1) : 0}%`, icon: FiTrendingUp, color: 'bg-amber-500' }
      ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'events', label: 'All Events' },
    ...(isAdmin ? [{ id: 'users', label: 'User Management' }, { id: 'hierarchy', label: 'Agent Hierarchy' }] : []),
    { id: 'analytics', label: 'Analytics' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-600 mt-1">Manage the entire platform from here</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <button
              type="button"
              onClick={loadAdminStats}
              disabled={statsLoading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} />
              Refresh stats
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/dashboard/events/create')}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            Create Event
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
              </div>
              <div className={`${stat.color} rounded-lg p-3`}>
                <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200">
          <nav className="flex flex-wrap gap-1 px-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Events</h3>
                  <div className="space-y-3">
                    {mappedEvents.slice(0, 5).map((event) => (
                      <div key={event.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">{event.title}</p>
                          <p className="text-sm text-slate-600">{event.date} • {event.registrations} registered</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(event.status)}`}>
                          {getStatusLabel(event.status)}
                        </span>
                      </div>
                    ))}
                    {mappedEvents.length === 0 && <p className="text-slate-500 py-4">No events yet</p>}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Top Performing Events</h3>
                  <div className="space-y-3">
                    {[...mappedEvents]
                      .sort((a, b) => (b.registrations ?? 0) - (a.registrations ?? 0))
                      .slice(0, 5)
                      .map((event) => (
                        <div key={event.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-900">{event.title}</p>
                            <p className="text-sm text-slate-600">{event.registrations} registrations</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-slate-900">${((event.price ?? 0) * (event.registrations ?? 0)).toLocaleString()}</p>
                            <p className="text-xs text-slate-500">revenue</p>
                          </div>
                        </div>
                      ))}
                    {mappedEvents.length === 0 && <p className="text-slate-500 py-4">No events yet</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'events' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-slate-900">All Events</h3>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/events/create')}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Create New Event
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Event</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Agent</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Registrations</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {mappedEvents.map((event) => (
                      <tr key={event.id} className="hover:bg-slate-50">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-slate-900">{event.title}</p>
                            <p className="text-sm text-slate-500">{event.date}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600">Agent #{event.agentId ?? '—'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900">{event.registrations}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-900">${((event.price ?? 0) * (event.registrations ?? 0)).toLocaleString()}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(event.status)}`}>
                            {getStatusLabel(event.status)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => navigate(`/dashboard/events/${event.id}`)} className="text-slate-400 hover:text-emerald-600 transition-colors">
                              <SafeIcon icon={FiEye} className="w-4 h-4" />
                            </button>
                            <button type="button" onClick={() => navigate(`/dashboard/events/${event.id}/edit`)} className="text-slate-400 hover:text-emerald-600 transition-colors">
                              <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {mappedEvents.length === 0 && (
                  <div className="text-center py-12 text-slate-500">No events yet</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'users' && isAdmin && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">User Management</h3>
              <p className="text-sm text-slate-600">List all users and update role (admin, agent, partner, student) and status (active, inactive, suspended).</p>
              {usersLoading ? (
                <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-slate-200 border-t-emerald-600 rounded-full animate-spin" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-900">{u.email ?? '—'}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{u.full_name ?? u.fullName ?? '—'}</td>
                          <td className="px-4 py-3"><span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{u.role ?? '—'}</span></td>
                          <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${u.status === 'active' ? 'bg-emerald-100 text-emerald-800' : u.status === 'suspended' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{u.status ?? '—'}</span></td>
                          <td className="px-4 py-3">
                            <button type="button" onClick={() => openEditUser(u)} className="text-emerald-600 hover:underline text-sm font-medium">Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && !usersLoading && <p className="text-slate-500 py-8 text-center">No users found (RLS may restrict).</p>}
                </div>
              )}

              {editingUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-semibold text-slate-900">Edit user</h4>
                      <button type="button" onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-slate-600"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">{editingUser.email}</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                        <select value={editRole} onChange={(e) => setEditRole(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2">
                          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                        <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2">
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                      <button type="button" onClick={() => setEditingUser(null)} className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">Cancel</button>
                      <button type="button" onClick={handleSaveUser} disabled={savingUser} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                        <SafeIcon icon={FiSave} className="w-4 h-4" /> {savingUser ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </motion.div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'hierarchy' && isAdmin && (
            <div className="space-y-4">
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Agent Hierarchy</h3>
                  <p className="text-sm text-slate-600">Add sub-agent rows and view hierarchy (parent_agent_id → child_user_id).</p>
                </div>
                <button type="button" onClick={() => setShowAddSubAgent(true)} className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700">
                  <SafeIcon icon={FiUserPlus} className="w-4 h-4" /> Add sub-agent
                </button>
              </div>
              {hierarchyLoading ? (
                <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-slate-200 border-t-emerald-600 rounded-full animate-spin" /></div>
              ) : (
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Parent Agent ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Child User ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Relationship</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {hierarchy.map((row, idx) => (
                        <tr key={row.id || idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-mono text-slate-700">{row.parent_agent_id ?? '—'}</td>
                          <td className="px-4 py-3 text-sm font-mono text-slate-700">{row.child_user_id ?? '—'}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{row.relationship_type ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {hierarchy.length === 0 && !hierarchyLoading && <p className="text-slate-500 py-8 text-center">No hierarchy rows. Table may be <code className="bg-slate-100 px-1 rounded">agent_hierarchies</code> (or <code className="bg-slate-100 px-1 rounded">agenthierarchies</code>).</p>}
                </div>
              )}

              {showAddSubAgent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-semibold text-slate-900">Add sub-agent</h4>
                      <button type="button" onClick={() => setShowAddSubAgent(false)} className="text-slate-400 hover:text-slate-600"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Parent agent ID (user UUID)</label>
                        <input type="text" value={subAgentParentId} onChange={(e) => setSubAgentParentId(e.target.value)} placeholder="UUID" className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Child user ID (user UUID)</label>
                        <input type="text" value={subAgentChildId} onChange={(e) => setSubAgentChildId(e.target.value)} placeholder="UUID" className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Relationship type</label>
                        <input type="text" value={subAgentRelationship} onChange={(e) => setSubAgentRelationship(e.target.value)} placeholder="sub_agent" className="w-full border border-slate-300 rounded-lg px-3 py-2" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                      <button type="button" onClick={() => setShowAddSubAgent(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">Cancel</button>
                      <button type="button" onClick={handleAddSubAgent} disabled={savingHierarchy} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">{savingHierarchy ? 'Adding...' : 'Add'}</button>
                    </div>
                  </motion.div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">Analytics & Reports</h3>
              <p className="text-slate-600">View detailed analytics on the Analytics page.</p>
              <button type="button" onClick={() => navigate('/dashboard/analytics')} className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700">
                Open Analytics
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
